import Image from 'next/image';
import { Trans, useTranslation } from 'next-i18next';
import * as React from 'react';
import { UrlObject } from 'url';

import { useConstructUrl } from '@/utils/construct-url';

import ContentPartnerWave from '../../../public/images/color-waves-contentpartner-sm.svg';
import Iphone from '../../../public/images/iphone.svg';
import { useQueryParams } from '../use-query-params';

interface UXHooks {
  create: {
    existingAccount: {
      description: string;
      link: {
        text: string;
        url: UrlObject;
      };
    };
    footer?: React.ReactNode;
    h1: string;
    secondaryText?: React.ReactNode;
  };
  main: {
    footer?: React.ReactNode;
    h1: string;
    noAccount: {
      description: string;
      link: {
        text: string;
        url: UrlObject;
      };
    };
    secondaryText?: React.ReactNode;
  };
  sidebar: {
    backgroundColor: string;
    button?: {
      text: string;
      url: string;
    };
    phone: React.ReactNode;
    tagline: {
      bold?: boolean;
      font?: 'primary' | 'secondary';
      text: string;
    };
    wave?: React.ReactElement;
  };
}

/**
 * A hook system for conditionally changing parts of the ux based on the `intent` query param.
 *
 * @example
 * Sidebar
 * ```typescriptreact
 * import { useUXHooks } from '@/hooks';
 *
 * const SidebarThing = () => {
 *   const { sidebar } = useUXHooks();
 *
 *   return <p fontFamily={sidebar.tagline.font}>{sidebar.tagline.text}</p>
 * }
 * ```
 *
 * Main
 * ```typescriptreact
 * import { useUXHooks } from '@/hooks';
 *
 * const MainThing = () => {
 *   const { main } = useUXHooks();
 *
 *   return <h1>{main.h1}</h1>
 * }
 * ```
 */
export const useUXHooks = () => {
  const { intent } = useQueryParams();
  const { t } = useTranslation();

  const loginUrl = useConstructUrl('/', { type: 'add_account' });

  const createAccountURL = useConstructUrl('/create-account');

  const defaults: UXHooks = {
    create: {
      existingAccount: {
        description: t('createAccount:already_have_account'),
        link: {
          text: t('sign_in'),
          url: loginUrl,
        },
      },
      h1: '',
    },
    main: {
      h1: '',
      noAccount: {
        description: t('dont_have_account'),
        link: {
          text: t('get_started'),
          url: createAccountURL,
        },
      },
    },

    sidebar: {
      backgroundColor: 'bg-black',
      button: {
        text: t('get_the_app'),
        url: 'https://www.bible.com/app',
      },
      phone: (
        <div className='pli-8'>
          <Image alt='' className='max-w-full h-auto' height={512} src='/images/youversion-hero.png' width={320} />{' '}
        </div>
      ),
      tagline: {
        bold: true,
        text: t('tagline'),
      },
    },
  };

  const ux: UXHooks = {
    ...defaults,
  };

  /**
   * To conditionally hook into certain parts of the UX, add a switch case and modify properties of the `ux` object.
   * See the `churches` case for an example of how to do it.
   *
   * @internal
   */
  switch (intent) {
    case 'churches': {
      ux.sidebar = {
        backgroundColor: 'bg-black',
        phone: (
          <div className='mbs-12 mli-auto pli-8'>
            <Iphone />
          </div>
        ),
        tagline: {
          font: 'secondary',
          text: t('intent.churches.tagline'),
        },
      };
      ux.main.footer = (
        <a className='no-underline text-text-light' href='https://support.youversion.church'>
          {t('intent.churches.footer')}
        </a>
      );
      ux.main.secondaryText = (
        <Trans
          components={{
            a: <a className='no-underline text-text-light' href='https://portal.youversion.church' />,
          }}
          i18nKey='intent.churches.secondary_text'
        />
      );
      ux.create.secondaryText = t('createAccount:intent.churches.create_an_account');
      break;
    }
    case 'content-partners': {
      ux.sidebar = {
        backgroundColor: 'bg-canvas-secondary-dark',
        phone: (
          <div className='-mbe-1 mli-auto pli-8 '>
            <Image
              alt=''
              className='max-w-full h-auto'
              height={512}
              src='/images/content-partners-hero.png'
              width={320}
            />
          </div>
        ),
        tagline: {
          font: 'secondary',
          text: t('intent.content_partners.tagline'),
        },
        wave: <ContentPartnerWave />,
      };
      ux.main.noAccount.link = {
        text: t('create_free_account'),
        url: createAccountURL,
      };
      ux.main.footer = (
        <a className='text-text-muted-light dark:text-text-muted-dark' href='https://support.youversion.church'>
          {t('intent.content_partners.footer')}
        </a>
      );
    }
    default:
      break;
  }

  return ux;
};
